/**
 * PLANTING (2026-10-04) — the DRAWING side of a plant object: its plan symbol (the
 * drafting convention per form: a tree canopy, a shrub cloud, a grass tuft, a palm star)
 * and the simple 3D STAND-IN the worker builds (a trunk and a faceted canopy). The
 * stand-in is what sections, elevations, picking, selection and walk collision read;
 * the viewer draws the stylized swaying plant over it (frontend/src/bim/plants/) and
 * keeps the stand-in itself invisible in 3D.
 *
 * Local frame (objectKinds.ts): x across the width centered on 0, y = 0..d from the
 * back-center anchor, z up. Every plant is centered at (0, d/2).
 */
import type { Pt2 } from '@aj/bim-kernel'
import { plantSpeciesOf, type PlantForm } from '@aj/bim-kernel'
import { MeshBuilder } from '../../extrude'
import type { PlanSymbolPath } from '../opening/operationSymbols'

const C = 'object.symbol'

/** deterministic 0..1 noise from an integer seed + index (symbols must not flicker) */
function n01(seed: number, i: number): number {
  const x = Math.sin(seed * 12.9898 + i * 78.233) * 43758.5453
  return x - Math.floor(x)
}
function seedOf(s: string): number {
  let h = 7
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 9973
  return h
}

/** a closed ring around (cx, cy) whose radius wobbles: `lobes` scallops of depth `amp` */
function lobed(cx: number, cy: number, rx: number, ry: number, lobes: number, amp: number, seed: number, segs = 72): PlanSymbolPath {
  const pts: Pt2[] = []
  for (let i = 0; i < segs; i++) {
    const a = (Math.PI * 2 * i) / segs
    const k = 1 - amp * (0.5 - 0.5 * Math.cos(a * lobes)) * (0.75 + 0.5 * n01(seed, Math.floor((a * lobes) / (Math.PI * 2))))
    pts.push([cx + Math.cos(a) * rx * k, cy + Math.sin(a) * ry * k])
  }
  return { pts, closed: true, component: C }
}
function circle(cx: number, cy: number, r: number, segs = 24): PlanSymbolPath {
  const pts: Pt2[] = []
  for (let i = 0; i < segs; i++) { const a = (Math.PI * 2 * i) / segs; pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]) }
  return { pts, closed: true, component: C }
}
function seg(a: Pt2, b: Pt2): PlanSymbolPath { return { pts: [a, b], closed: false, component: C } }

/** the plan symbol for a plant type, in the object's local frame */
export function plantPlanSymbol(speciesId: unknown, w: number, d: number): PlanSymbolPath[] {
  const sp = plantSpeciesOf(speciesId)
  const cx = 0, cy = d / 2, rx = w / 2, ry = d / 2
  const r = Math.min(rx, ry)
  const seed = seedOf(sp.id)
  const out: PlanSymbolPath[] = []
  switch (sp.form as PlantForm) {
    case 'tree': {
      // canopy with a soft scalloped edge, branches radiating from the trunk
      out.push(lobed(cx, cy, rx, ry, 9, 0.12, seed))
      out.push(circle(cx, cy, Math.max(0.25, r * 0.05), 12))
      const n = 7
      for (let i = 0; i < n; i++) {
        const a = (Math.PI * 2 * (i + 0.3 * n01(seed, i))) / n
        const t = 0.55 + 0.25 * n01(seed, i + 9)
        out.push(seg([cx + Math.cos(a) * r * 0.08, cy + Math.sin(a) * r * 0.08], [cx + Math.cos(a) * r * t, cy + Math.sin(a) * r * t]))
      }
      break
    }
    case 'conifer': {
      // a star of needle points around a trunk dot
      const pts: Pt2[] = []
      const n = 28
      for (let i = 0; i < n * 2; i++) {
        const a = (Math.PI * i) / n
        const k = i % 2 ? 0.8 : 1
        pts.push([cx + Math.cos(a) * rx * k, cy + Math.sin(a) * ry * k])
      }
      out.push({ pts, closed: true, component: C })
      out.push(circle(cx, cy, Math.max(0.3, r * 0.06), 12))
      out.push(circle(cx, cy, r * 0.45, 24))
      break
    }
    case 'column': {
      out.push(circle(cx, cy, r, 24))
      out.push(circle(cx, cy, r * 0.55, 18))
      break
    }
    case 'palm': {
      // fronds radiating from the crown, each a narrow leaf outline
      out.push(circle(cx, cy, Math.max(0.45, r * 0.08), 14))
      const n = 11
      for (let i = 0; i < n; i++) {
        const a = (Math.PI * 2 * i) / n + 0.2 * n01(seed, i)
        const L = r * (0.85 + 0.15 * n01(seed, i + 4))
        const ux = Math.cos(a), uy = Math.sin(a), px = -uy, py = ux
        const wd = r * 0.07
        out.push({
          pts: [[cx, cy], [cx + ux * L * 0.5 + px * wd, cy + uy * L * 0.5 + py * wd], [cx + ux * L, cy + uy * L], [cx + ux * L * 0.5 - px * wd, cy + uy * L * 0.5 - py * wd]],
          closed: true, component: C,
        })
      }
      break
    }
    case 'shrub': {
      out.push(lobed(cx, cy, rx, ry, 7, 0.18, seed))
      out.push(lobed(cx, cy, rx * 0.5, ry * 0.5, 5, 0.2, seed + 3, 40))
      break
    }
    case 'topiary': {
      out.push(circle(cx, cy, r, 28))
      out.push(lobed(cx, cy, r * 0.62, r * 0.62, 6, 0.12, seed, 40))
      break
    }
    case 'hedge': {
      // a clipped run: the outline with scalloped long faces
      const pts: Pt2[] = []
      const n = Math.max(4, Math.round(w / 1.2))
      const amp = Math.min(0.12, d * 0.08)
      for (let i = 0; i <= n; i++) pts.push([-rx + (w * i) / n, (i % 2 ? amp : 0)])
      for (let i = n; i >= 0; i--) pts.push([-rx + (w * i) / n, d - (i % 2 ? amp : 0)])
      out.push({ pts, closed: true, component: C })
      break
    }
    case 'grass':
    case 'fern': {
      // a tuft: blades fanning out from the crown
      const n = sp.form === 'fern' ? 7 : 16
      for (let i = 0; i < n; i++) {
        const a = (Math.PI * 2 * i) / n + 0.3 * n01(seed, i)
        const L = r * (0.7 + 0.3 * n01(seed, i + 11))
        out.push(seg([cx + Math.cos(a) * r * 0.1, cy + Math.sin(a) * r * 0.1], [cx + Math.cos(a) * L, cy + Math.sin(a) * L]))
      }
      if (sp.form === 'grass') out.push(circle(cx, cy, r, 20))
      break
    }
    case 'rosette': {
      // pointed leaves from the center (agave)
      const n = 12
      for (let i = 0; i < n; i++) {
        const a = (Math.PI * 2 * i) / n
        const ux = Math.cos(a), uy = Math.sin(a), px = -uy, py = ux, wd = r * 0.16
        out.push({
          pts: [[cx, cy], [cx + ux * r * 0.45 + px * wd, cy + uy * r * 0.45 + py * wd], [cx + ux * r, cy + uy * r], [cx + ux * r * 0.45 - px * wd, cy + uy * r * 0.45 - py * wd]],
          closed: true, component: C,
        })
      }
      break
    }
  }
  return out
}

/** a faceted ellipsoid (canopy, mound) centered (cx, cy, cz) with radii (rx, ry, rz) */
function ellipsoid(b: MeshBuilder, cx: number, cy: number, cz: number, rx: number, ry: number, rz: number, seg = 10, ring = 6) {
  const P = (i: number, j: number): [number[], number[]] => {
    const th = (Math.PI * 2 * i) / seg, ph = (Math.PI * j) / ring - Math.PI / 2
    const nx = Math.cos(ph) * Math.cos(th), ny = Math.cos(ph) * Math.sin(th), nz = Math.sin(ph)
    const nl = Math.hypot(nx / rx, ny / ry, nz / rz) || 1
    return [[cx + nx * rx, cy + ny * ry, cz + nz * rz], [nx / rx / nl, ny / ry / nl, nz / rz / nl]]
  }
  for (let j = 0; j < ring; j++) {
    for (let i = 0; i < seg; i++) {
      const [a, na] = P(i, j), [b2, nb] = P(i + 1, j), [c, nc] = P(i + 1, j + 1), [dd, nd] = P(i, j + 1)
      if (j > 0) b.addTriN(a, b2, c, na, nb, nc)
      if (j < ring - 1) b.addTriN(a, c, dd, na, nc, nd)
    }
  }
}
/** an upright tapered cylinder from z0 to z1 */
function trunk(b: MeshBuilder, cx: number, cy: number, z0: number, z1: number, r0: number, r1: number, seg = 8) {
  for (let i = 0; i < seg; i++) {
    const a0 = (Math.PI * 2 * i) / seg, a1 = (Math.PI * 2 * (i + 1)) / seg
    const n0 = [Math.cos(a0), Math.sin(a0), 0], n1 = [Math.cos(a1), Math.sin(a1), 0]
    const p00 = [cx + n0[0] * r0, cy + n0[1] * r0, z0], p10 = [cx + n1[0] * r0, cy + n1[1] * r0, z0]
    const p01 = [cx + n0[0] * r1, cy + n0[1] * r1, z1], p11 = [cx + n1[0] * r1, cy + n1[1] * r1, z1]
    b.addTriN(p00, p10, p11, n0, n1, n1)
    b.addTriN(p00, p11, p01, n0, n1, n0)
  }
}

/** the 3D stand-in, sized to the type's width × depth × height */
export function buildPlantProxy(speciesId: unknown, w: number, d: number, h: number, b: MeshBuilder): void {
  const sp = plantSpeciesOf(speciesId)
  const cx = 0, cy = d / 2, rx = w / 2, ry = d / 2
  switch (sp.form as PlantForm) {
    case 'tree': {
      const crownBase = h * 0.38
      trunk(b, cx, cy, 0, crownBase + (h - crownBase) * 0.3, Math.max(0.25, w * 0.025), Math.max(0.15, w * 0.015))
      ellipsoid(b, cx, cy, crownBase + (h - crownBase) / 2, rx, ry, (h - crownBase) / 2)
      break
    }
    case 'conifer': {
      const crownBase = h * 0.2
      trunk(b, cx, cy, 0, crownBase + 1, Math.max(0.4, w * 0.03), Math.max(0.3, w * 0.025))
      // a cone of stacked faceted rings
      const tiers = 5
      for (let t = 0; t < tiers; t++) {
        const z0 = crownBase + ((h - crownBase) * t) / tiers, z1 = Math.min(h, crownBase + ((h - crownBase) * (t + 1.3)) / tiers)
        const k = 1 - t / tiers
        ellipsoid(b, cx, cy, (z0 + z1) / 2, rx * k, ry * k, (z1 - z0) / 2, 10, 4)
      }
      break
    }
    case 'column':
      if (sp.crown === 'round') {
        // a clipped screening column (the Indian laurel fig): full width from near the
        // ground to the top, stacked rounded lobes and a blunt rounded crown — not a spire
        // overlapping lobes, the lowest sitting on the ground and the top one ending at h
        const n = Math.max(3, Math.round(h / (rx * 1.6)))
        const hh = Math.min(h / 2, (h / n) * 0.75)
        for (let i = 0; i < n; i++) {
          const zc = n === 1 ? h / 2 : hh + ((h - 2 * hh) * i) / (n - 1)
          ellipsoid(b, cx, cy, zc, rx, ry, hh, 10, 6)
        }
        break
      }
      trunk(b, cx, cy, 0, h * 0.1, rx * 0.15, rx * 0.12)
      ellipsoid(b, cx, cy, h * 0.53, rx, ry, h * 0.47, 10, 8)
      break
    case 'palm': {
      const crown = Math.min(h * 0.3, w * 0.35)
      trunk(b, cx, cy, 0, h - crown * 0.5, Math.max(0.6, w * 0.05), Math.max(0.5, w * 0.045))
      ellipsoid(b, cx, cy, h - crown * 0.5, rx, ry, crown * 0.5, 12, 4)
      break
    }
    case 'hedge': {
      // a rounded box: one ellipsoid per run segment keeps it readable in elevation
      const n = Math.max(1, Math.round(w / Math.max(1, d)))
      for (let i = 0; i < n; i++) ellipsoid(b, -rx + (w * (i + 0.5)) / n, cy, h / 2, (w / n) * 0.62, ry, h / 2, 8, 5)
      break
    }
    case 'topiary':
      ellipsoid(b, cx, cy, h / 2, rx, ry, h / 2)
      break
    default:
      // shrubs, grasses, rosettes, ferns: a mound sitting on the ground
      ellipsoid(b, cx, cy, h / 2, rx, ry, h / 2, 10, 5)
  }
}
